import { showToast } from "@/app/slices/statusSlice";
import { AnalyticsPeriod } from "@tumtutor/shared/type/analytics.type";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import LoadingSpinnerComponent from "../../components/LoadingSpinnerComponent";
import Header from "../../components/header/Header";
import { useToastEffect } from "../../hooks";
import {
  useGetChatAnalyticsQuery,
  useGetCourseQuery,
  useGetDashboardQuery,
} from "../../services/courseApi";
import ChatTopicCards from "./ChatTopicCards";
import DatePicker from "./DatePicker";

function DashboardPage() {
  const { t } = useTranslation(["dashboardPage", "header"]);
  const dispatch = useDispatch();
  const courseId = useParams().courseId ?? "";
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date(),
  );
  const [selectedType, setSelectedType] = useState<AnalyticsPeriod>("daily");
  const [queryDate, setQueryDate] = useState<Date>();
  const [queryType, setQueryType] = useState<AnalyticsPeriod>("daily");
  const { data: course } = useGetCourseQuery(courseId);
  const {
    data: iFrameURL,
    isError,
    error,
    isFetching: isDashboardFetching,
  } = useGetDashboardQuery(courseId);

  const {
    data: chatAnalytics,
    error: chatAnalyticsError,
    isError: chatAnalyticsIsError,
    isFetching: isChatAnalyticsFetching,
  } = useGetChatAnalyticsQuery(
    { courseId: courseId, date: queryDate ?? new Date(), type: queryType },
    { skip: !courseId || !queryDate },
  );

  const handleSubmit = () => {
    if (!selectedDate) {
      dispatch(
        showToast({
          message: t("chatAnalytics.noDateSelected"),
          type: "info",
        }),
      );
      return;
    }

    if (selectedDate >= new Date(new Date().toDateString())) {
      dispatch(
        showToast({
          message: t("chatAnalytics.futureDateSelected"),
          type: "info",
        }),
      );
      return;
    }

    const adjustedDate = new Date(
      selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000,
    );
    setQueryDate(adjustedDate);
    setQueryType(selectedType);
  };

  useToastEffect({ isError, error });
  useToastEffect({ isError: chatAnalyticsIsError, error: chatAnalyticsError });

  const isLoading = isDashboardFetching || isChatAnalyticsFetching;

  return (
    <Header
      breadcrumbs={[
        {
          name:
            course?.name ?? t("breadcrumbFallback.course", { ns: "header" }),
          link: `/course/${courseId}`,
        },
        {
          name: t("dashboard.title"),
        },
      ]}
      backButtonRoute={`/course/${courseId}`}
    >
      <div className="flex flex-col items-center gap-4">
        <div className="w-full md:w-4/5">
          <h1 className="font-bold text-3xl">{t("chatAnalytics.title")}</h1>
          <p>{t("chatAnalytics.subtitle")}</p>
        </div>
        <div className="flex w-full flex-col justify-between gap-4 md:w-4/5">
          <div className="flex w-full items-center justify-start gap-4">
            <DatePicker
              selectedDate={selectedDate}
              onSelect={setSelectedDate}
            />
            <div className="flex items-center gap-2">
              {(["daily", "weekly", "monthly"] as const).map((type) => (
                <label key={type} className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="queryType"
                    value={type}
                    checked={selectedType === type}
                    onChange={() => setSelectedType(type)}
                    className="radio radio-primary radio-sm"
                  />
                  {t(`chatAnalytics.${type}`)}
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              className="btn btn-primary btn-sm"
            >
              {t("chatAnalytics.submit")}
            </button>
          </div>
          {isLoading ? <LoadingSpinnerComponent /> : null}
          {chatAnalytics?.map((chat, index) => (
            <ChatTopicCards key={chat.topic} chat={chat} topicNumber={index} />
          ))}
          {queryDate &&
            !isChatAnalyticsFetching &&
            (!chatAnalytics || chatAnalytics.length === 0) && (
              <div className="alert alert-warning">
                {t("chatAnalytics.tooFewMessages")}
              </div>
            )}
        </div>
      </div>
      <div className="mt-4 flex flex-col items-center gap-4">
        <div className="flex w-full flex-col items-start md:w-4/5">
          <h1 className="font-bold text-3xl">{t("dashboard.title")}</h1>
          <p>{t("dashboard.subtitle")}</p>
        </div>
        <iframe
          src={iFrameURL}
          title="dashboard"
          className="h-[80vh] w-full md:w-4/5"
        />
      </div>
    </Header>
  );
}

export default DashboardPage;
